import React, { useEffect, useRef } from 'react';
import { HStack, VStack, Text, IconButton } from '@cad-arc';
import { FlagRenderer } from '../../common/components/flag-renderer/flag-renderer';
import { handleArrowKeyButtonNav, isKeyboardActivatedClick } from './narrative-action-menu';

export function DeleteNarrativeConfirmation({
    messageText,
    isLoading = false,
    isFirstResponder = false,
    onConfirm,
    onClose,
}: {
    messageText?: string | null;
    isLoading?: boolean;
    isFirstResponder?: boolean;
    onConfirm: () => void;
    onClose: (isKeyboardActivated: boolean) => void;
}): React.JSX.Element {
    const cancelRef = useRef<HTMLButtonElement>(null);

    useEffect(function autoFocusCancelButton() {
        cancelRef.current?.focus();
    }, []);

    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
        if (event.key === 'Escape') {
            event.preventDefault();
            onClose(true);
            return;
        }
        handleArrowKeyButtonNav(event, event.currentTarget);
    };

    return (
        <HStack
            bg="negative.default"
            borderRadius="base"
            pl={isFirstResponder ? '36px' : '30px'}
            pr="6"
            py="3"
            marginLeft="var(--arc-space-4)"
            marginRight="calc(var(--arc-space-4) - var(--scrollbar-width))"
            spacing="3"
            align="start"
            onKeyDown={handleKeyDown}
            data-testid="DELETE_NARRATIVE_CONFIRMATION"
        >
            <VStack flex="1" minW="0" align="stretch" spacing="2" justify="center">
                <Text
                    fontSize={isFirstResponder ? 'lg' : 'sm'}
                    fontWeight="bold"
                    style={{ color: 'var(--arc-colors-negative-content)' }}
                >
                    Delete narrative message?
                </Text>
                <Text
                    as="div"
                    fontSize={isFirstResponder ? 'lg' : 'sm'}
                    style={{
                        color: 'var(--arc-colors-negative-content)',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                    }}
                >
                    <FlagRenderer text={messageText} isFirstResponder={isFirstResponder} />
                </Text>
            </VStack>
            <HStack spacing="1" align="center">
                <IconButton
                    ref={cancelRef}
                    aria-label="Cancel"
                    icon="CloseX"
                    variant="outline"
                    colorVariant="inverse"
                    disabled={isLoading}
                    onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                        onClose(isKeyboardActivatedClick(event));
                    }}
                />
                <IconButton
                    aria-label="Delete"
                    icon="Check"
                    variant="solid"
                    colorVariant="inverse"
                    isLoading={isLoading}
                    onClick={onConfirm}
                />
            </HStack>
        </HStack>
    );
}
